import type { CalibrationProfile } from "./types";

export const DEFAULT_CONTROL_SETTINGS = {
  sensitivity: { x: 0.9, y: 2.2 },
  smoothing: 0.24,
  flip: { x: false, y: true },
  viewport: {
    zoom: 2.15,
    pan: { x: 0.36, y: 0.36 },
    depthEffect: 0.28,
    framing: { x: -0.04, y: 0.19 },
  },
  laser: {
    color: "#ff375f",
    brightness: 1.45,
    durationMs: 680,
    width: 0.45,
  },
  balloon: {
    fenceTop: -0.09,
    fenceBottom: -0.27,
    yardDepthFt: 100,
    throwerDistanceFt: 16,
    fenceHeightFt: 8,
    initialScale: 0.32,
    finalScale: 1,
    arc: 1,
    flightTime: 1,
    cadence: 0.9,
    originScatter: 0.03,
    destinationScatter: { x: 0.75, y: 0.8 },
  },
  thresholds: {
    mouthFire: 0.19,
    mouthRelease: 0.11,
    blinkFire: 0.2,
    blinkRelease: 0.17,
    blinkHoldMs: 40,
  },
} satisfies Pick<
  CalibrationProfile,
  "sensitivity" | "smoothing" | "flip" | "viewport" | "laser" | "balloon" | "thresholds"
>;

export function createCalibrationProfile(
  values: Pick<CalibrationProfile, "neutral" | "range" | "trigger">,
): CalibrationProfile {
  return {
    schemaVersion: 6,
    ...values,
    sensitivity: { ...DEFAULT_CONTROL_SETTINGS.sensitivity },
    smoothing: DEFAULT_CONTROL_SETTINGS.smoothing,
    flip: { ...DEFAULT_CONTROL_SETTINGS.flip },
    viewport: {
      ...DEFAULT_CONTROL_SETTINGS.viewport,
      pan: { ...DEFAULT_CONTROL_SETTINGS.viewport.pan },
      framing: { ...DEFAULT_CONTROL_SETTINGS.viewport.framing },
    },
    laser: { ...DEFAULT_CONTROL_SETTINGS.laser },
    balloon: {
      ...DEFAULT_CONTROL_SETTINGS.balloon,
      destinationScatter: { ...DEFAULT_CONTROL_SETTINGS.balloon.destinationScatter },
    },
    thresholds: { ...DEFAULT_CONTROL_SETTINGS.thresholds },
    calibratedAt: Date.now(),
  };
}

export function createWebcamMappedProfile(trigger: CalibrationProfile["trigger"] = "blink"): CalibrationProfile {
  return createCalibrationProfile({
    neutral: { x: 0.5, y: 0.5, z: 0.24 },
    range: { left: 0.5, right: 0.5, up: 0.5, down: 0.5 },
    trigger,
  });
}

export function migrateCalibrationProfile(value: unknown): CalibrationProfile | null {
  if (!value || typeof value !== "object") return null;
  const input = value as any;
  if (!input.neutral || !input.range) return null;

  if (input.schemaVersion === 6 || input.schemaVersion === 5 || input.schemaVersion === 4 || input.schemaVersion === 3) {
    return {
      ...createCalibrationProfile({
        neutral: input.neutral,
        range: input.range,
        trigger: input.trigger ?? "mouth",
      }),
      ...input,
      schemaVersion: 6,
      sensitivity: { ...DEFAULT_CONTROL_SETTINGS.sensitivity, ...(input.sensitivity ?? {}) },
      flip: { ...DEFAULT_CONTROL_SETTINGS.flip, ...(input.flip ?? {}) },
      viewport: {
        ...DEFAULT_CONTROL_SETTINGS.viewport,
        ...(input.viewport ?? {}),
        pan: { ...DEFAULT_CONTROL_SETTINGS.viewport.pan, ...(input.viewport?.pan ?? {}) },
        framing: { ...DEFAULT_CONTROL_SETTINGS.viewport.framing, ...(input.viewport?.framing ?? {}) },
      },
      laser: { ...DEFAULT_CONTROL_SETTINGS.laser, ...(input.laser ?? {}) },
      balloon: {
        ...DEFAULT_CONTROL_SETTINGS.balloon,
        ...(input.balloon ?? {}),
        destinationScatter: {
          ...DEFAULT_CONTROL_SETTINGS.balloon.destinationScatter,
          ...(input.balloon?.destinationScatter ?? {}),
        },
      },
      thresholds: { ...DEFAULT_CONTROL_SETTINGS.thresholds, ...(input.thresholds ?? {}) },
    };
  }

  if (input.schemaVersion === 2) {
    return {
      ...createCalibrationProfile({
        neutral: input.neutral,
        range: input.range,
        trigger: input.trigger ?? "mouth",
      }),
      flip: { ...DEFAULT_CONTROL_SETTINGS.flip, ...(input.flip ?? {}) },
      thresholds: { ...DEFAULT_CONTROL_SETTINGS.thresholds, ...(input.thresholds ?? {}) },
      calibratedAt: input.calibratedAt ?? Date.now(),
    };
  }

  if (input.schemaVersion === 1) {
    const legacySensitivity = typeof input.sensitivity === "number" ? input.sensitivity : 1;
    return {
      ...createCalibrationProfile({
        neutral: input.neutral,
        range: input.range,
        trigger: input.trigger ?? "mouth",
      }),
      sensitivity: { x: legacySensitivity, y: legacySensitivity },
      smoothing: input.smoothing ?? DEFAULT_CONTROL_SETTINGS.smoothing,
      calibratedAt: input.calibratedAt ?? Date.now(),
    };
  }

  return null;
}

export function resetControlSettings(profile: CalibrationProfile): CalibrationProfile {
  return {
    ...profile,
    sensitivity: { ...DEFAULT_CONTROL_SETTINGS.sensitivity },
    smoothing: DEFAULT_CONTROL_SETTINGS.smoothing,
    flip: { ...DEFAULT_CONTROL_SETTINGS.flip },
    viewport: {
      ...DEFAULT_CONTROL_SETTINGS.viewport,
      pan: { ...DEFAULT_CONTROL_SETTINGS.viewport.pan },
      framing: { ...DEFAULT_CONTROL_SETTINGS.viewport.framing },
    },
    laser: { ...DEFAULT_CONTROL_SETTINGS.laser },
    balloon: {
      ...DEFAULT_CONTROL_SETTINGS.balloon,
      destinationScatter: { ...DEFAULT_CONTROL_SETTINGS.balloon.destinationScatter },
    },
    thresholds: { ...DEFAULT_CONTROL_SETTINGS.thresholds },
  };
}
